import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { rupiah, tanggalID, todayISO, toDateInput } from "@/lib/format";
import { PageHeader } from "@/components/app/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/app/CurrencyInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Search } from "lucide-react";
import Pagination, { usePagination } from "@/components/app/Pagination";

const empty = { tanggal: todayISO(), kategori_id: "", kategori_nama: "", nama_vendor: "", nominal: 0, keterangan: "" };

export default function Pengeluaran() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);
  const [kategori, setKategori] = useState([]);
  const [q, setQ] = useState(""); const [from, setFrom] = useState(""); const [to, setTo] = useState("");

  const load = async () => {
    const [a, k] = await Promise.all([api.get("/pengeluaran"), api.get("/kategori", { params: { tipe: "pengeluaran" } })]);
    setItems(a.data); setKategori(k.data);
  };
  useEffect(()=>{ load(); },[]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e && e.target ? e.target.value : e });

  const openNew = () => { setEditId(null); setForm({...empty, tanggal: todayISO()}); setOpen(true); };
  const openEdit = (it) => { setEditId(it.id); setForm({...empty, ...it, tanggal: toDateInput(it.tanggal) || todayISO(), keterangan: it.keterangan ?? ""}); setOpen(true); };

  const save = async () => {
    if (!form.tanggal) { toast.error("Tanggal wajib diisi"); return; }
    if (!form.nama_vendor?.trim()) { toast.error("Nama vendor/karyawan wajib diisi"); return; }
    if (!(Number(form.nominal) > 0)) { toast.error("Nominal wajib diisi (lebih dari 0)"); return; }
    const payload = { ...form, nominal: Number(form.nominal) };
    try {
      if (editId) await api.put(`/pengeluaran/${editId}`, payload);
      else await api.post("/pengeluaran", payload);
      toast.success(editId?"Data diperbarui":"Pengeluaran tersimpan");
      setOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const delOne = async (id) => {
    if (!confirm("Hapus data ini?")) return;
    await api.delete(`/pengeluaran/${id}`); toast.success("Terhapus"); load();
  };

  const toggle = (id) => { const s=new Set(selected); s.has(id)?s.delete(id):s.add(id); setSelected(s); };
  const filtered = items.filter(it => {
    const s = q.trim().toLowerCase();
    const okQ = !s || [it.kategori_nama, it.nama_vendor, it.tanggal].filter(Boolean).some(v=>String(v).toLowerCase().includes(s));
    return okQ && (!from || (it.tanggal||"") >= from) && (!to || (it.tanggal||"") <= to);
  });
  const selectableIds = filtered.filter(f => !f.auto_sync).map(f => f.id);
  const toggleAll = () => selected.size===selectableIds.length ? setSelected(new Set()) : setSelected(new Set(selectableIds));
  const { page, setPage, totalPages, paged, total, pageSize } = usePagination(filtered, 10);

  const bulkDel = async () => {
    if (!selected.size || !confirm(`Hapus ${selected.size} data?`)) return;
    await api.post("/pengeluaran/bulk-delete", { ids: Array.from(selected) });
    setSelected(new Set()); await load(); toast.success("Terhapus");
  };

  return (
    <>
      <PageHeader title="Pengeluaran" subtitle="Catat pengeluaran operasional & pembayaran vendor/karyawan." actions={
        <>
          {selected.size>0 && <Button variant="outline" onClick={bulkDel} className="rounded-sm" data-testid="bulk-del-pengeluaran"><Trash2 size={14} className="mr-1"/> Hapus ({selected.size})</Button>}
          <Button onClick={openNew} className="brand-btn rounded-sm" data-testid="add-pengeluaran-btn"><Plus size={14} className="mr-1"/> Tambah</Button>
        </>
      }/>
      <div className="p-4 md:p-8 space-y-4">
        <div className="card-flat p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2 relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <Input className="pl-9" placeholder="Cari kategori, vendor, tanggal..." value={q} onChange={(e)=>setQ(e.target.value)} data-testid="pgl-search"/>
          </div>
          <Input type="date" value={from} onChange={(e)=>setFrom(e.target.value)}/>
          <Input type="date" value={to} onChange={(e)=>setTo(e.target.value)}/>
        </div>
        <div className="card-flat overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-xs md:text-sm" data-testid="pengeluaran-table">
            <thead><tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] uppercase tracking-wide text-slate-500">
              <th className="p-3 w-10"><Checkbox checked={filtered.length>0 && selected.size===filtered.length} onCheckedChange={toggleAll}/></th>
              <th className="p-3">Tanggal</th><th className="p-3">Kategori</th><th className="p-3">Vendor</th>
              <th className="p-3 text-right">Nominal</th><th className="p-3 hide-mobile">Keterangan</th>
              <th className="p-3 text-right">Aksi</th>
            </tr></thead>
            <tbody>
              {filtered.length===0 && <tr><td colSpan="7" className="text-center py-10 text-slate-400">Tidak ada data.</td></tr>}
              {paged.map(p=>(
                <tr key={p.id} className={`border-b border-slate-100 hover:bg-slate-50 ${p.auto_sync?"bg-blue-50/40":""}`}>
                  <td className="p-3">{p.auto_sync ? <span className="opacity-30"><Checkbox disabled/></span> : <Checkbox checked={selected.has(p.id)} onCheckedChange={()=>toggle(p.id)}/>}</td>
                  <td className="p-3 whitespace-nowrap">{tanggalID(p.tanggal)}</td>
                  <td className="p-3">
                    {p.kategori_nama||"-"}
                    {p.auto_sync && <span className="ml-2 text-[10px] px-1.5 py-0.5 brand-soft brand-text rounded-sm font-mono">AUTO-SYNC</span>}
                  </td>
                  <td className="p-3">{p.nama_vendor}</td>
                  <td className="p-3 text-right font-mono font-semibold">{rupiah(p.nominal)}</td>
                  <td className="p-3 text-slate-500 hide-mobile">{p.keterangan||"-"}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-1 justify-end">
                      {p.auto_sync ? (
                        <span className="text-[10px] text-slate-400 italic px-2">Kelola via Produksi</span>
                      ) : (
                        <>
                          <button onClick={()=>openEdit(p)} className="p-1.5 rounded-sm hover:bg-slate-100" data-testid={`edit-pgl-${p.id}`}><Pencil size={14}/></button>
                          <button onClick={()=>delOne(p.id)} className="p-1.5 rounded-sm hover:bg-rose-50" data-testid={`del-pgl-${p.id}`}><Trash2 size={14} className="text-rose-600"/></button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <Pagination page={page} setPage={setPage} totalPages={totalPages} total={total} pageSize={pageSize} testid="pengeluaran-pagination"/>
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="pengeluaran-dialog">
          <DialogHeader><DialogTitle>{editId?"Ubah":"Tambah"} Pengeluaran</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Tanggal <span className="text-rose-500">*</span></Label><Input type="date" value={form.tanggal} onChange={upd("tanggal")}/></div>
            <div className="space-y-1.5">
              <Label>Kategori</Label>
              <Select value={form.kategori_id} onValueChange={(v)=>{const k=kategori.find(x=>x.id===v);setForm({...form,kategori_id:v,kategori_nama:k?.nama||""});}}>
                <SelectTrigger><SelectValue placeholder="Pilih kategori"/></SelectTrigger>
                <SelectContent>{kategori.map(k=><SelectItem key={k.id} value={k.id}>{k.nama}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-1 md:col-span-2 space-y-1.5"><Label>Nama Vendor / Karyawan <span className="text-rose-500">*</span></Label><Input value={form.nama_vendor} onChange={upd("nama_vendor")}/></div>
            <div className="col-span-1 md:col-span-2 space-y-1.5"><Label>Nominal (Rp) <span className="text-rose-500">*</span></Label><CurrencyInput value={form.nominal} onValueChange={upd("nominal")}/></div>
            <div className="col-span-1 md:col-span-2 space-y-1.5"><Label>Keterangan</Label><Textarea value={form.keterangan} onChange={upd("keterangan")}/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)} className="rounded-sm">Batal</Button>
            <Button onClick={save} className="brand-btn rounded-sm" data-testid="pengeluaran-save">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
